import { upperFirst } from 'lodash/fp';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import {
  CheckMetadataItemType,
  GroupedCheckMetadataItem,
} from 'dashboard/components/ChecksTable/computeCheckMetadata';
import ImagesComparison from 'dashboard/components/ImagesComparison';
import InquiryStatusSticker from 'dashboard/components/InquiryStatusSticker';
import AsyncImage from 'dashboard/components-lib/AsyncImage';
import {
  GovernmentIdInconsistentMatch,
  IdInconsistentRepeatDetection,
} from 'dashboard/models/check';
import { InquiryStatus } from 'dashboard/models/inquiry';

const UnorderedList = styled('ul')`
  list-style-type: none;
`;

const ListItem = styled('li')`
  margin: 0;
  font-size: ${({ theme }) => theme.text.paragraph.md.fontSize};
  line-height: ${({ theme }) => theme.text.paragraph.md.lineHeight};
`;

/**
 * identification_number -> Identification Number
 */
function snakeToUpper(attribute: string): string {
  return attribute.split('_').map(upperFirst).join(' ');
}

export const computeIdInconsistentRepeatDetectionMetadata = (
  check: IdInconsistentRepeatDetection,
  frontPhotoUrl?: string,
  backPhotoUrl?: string,
) => {
  const matches = check.metadata?.matches ?? [];

  // Generate match details for each matched ID
  // Failed, or passed (with metadata)
  const governmentIdRegistryMatches: GroupedCheckMetadataItem[] = matches
    .map((match, idx: number): GroupedCheckMetadataItem | null => {
      // NOTE: If Redacted, images unavailable
      if (!match['document-front-photo-url'] && !match['document-back-photo-url']) return null;

      const comparisonModal = makeImagesComparisonModal(match, frontPhotoUrl, backPhotoUrl);

      return {
        type: CheckMetadataItemType.Group,
        heading: { title: `Matched ID ${idx + 1}` },
        items: [
          match['verification-id'] && {
            label: 'Verification',
            value: (
              <Link to={`/dashboard/verifications/${match['verification-id']}`}>
                {match['verification-id']}
              </Link>
            ),
          },
          match['inquiry-id'] && {
            label: 'Inquiry',
            value: (
              <span>
                <Link to={`/dashboard/inquiries/${match['inquiry-id']}`}>
                  {match['inquiry-id']}
                </Link>
                &nbsp;
                {match['inquiry-status'] && (
                  <InquiryStatusSticker status={match['inquiry-status'] as InquiryStatus} />
                )}
              </span>
            ),
          },
          match['attributes-mismatched']?.length && {
            label: 'Matched Attributes',
            value: (
              <UnorderedList>
                {match['attributes-mismatched'].map((attribute) => (
                  <ListItem key={attribute}>{snakeToUpper(attribute)}</ListItem>
                ))}
              </UnorderedList>
            ),
          },
          match['document-front-photo-url'] && {
            label: 'Document Front Photo',
            value: (
              <AsyncImage
                imageUrl={match['document-front-photo-url']}
                width='400px'
                height='250px'
                modalLabel='Inconsistent Repeat Detection'
                modalContents={comparisonModal}
              />
            ),
          },
          match['document-back-photo-url'] && {
            label: 'Document Back Photo',
            value: (
              <AsyncImage
                imageUrl={match['document-back-photo-url']}
                width='400px'
                height='250px'
                modalLabel='Inconsistent Repeat Detection'
                modalContents={comparisonModal}
              />
            ),
          },
        ].filter(Boolean) as GroupedCheckMetadataItem['items'],
      };
    })
    // NOTE: ts can't infer GroupedCheckMetadataItem | null -> GroupedCheckMetadataItem on its own
    .filter(Boolean) as GroupedCheckMetadataItem[];

  return {
    metadata: governmentIdRegistryMatches,
    hideIfVerificationPassed: false,
  };
};

/**
 * Generates an image comparison between submitted image(s) and matched image(s); activated when
 * the user clicks on the matched image.
 */
function makeImagesComparisonModal(
  match: GovernmentIdInconsistentMatch,
  submittedFrontUrl?: string,
  submittedBackUrl?: string,
) {
  const leftImageUrls: string[] = [];
  const leftImageFooterTexts = [];
  const rightImageUrls = [];
  const rightImageFooterTexts = [];

  if (submittedFrontUrl) {
    leftImageUrls.push(submittedFrontUrl);
    leftImageFooterTexts.push('Submitted ID front');
  }

  if (submittedBackUrl) {
    leftImageUrls.push(submittedBackUrl);
    leftImageFooterTexts.push('Submitted ID back');
  }

  if (match['document-front-photo-url']) {
    rightImageUrls.push(match['document-front-photo-url']);
    rightImageFooterTexts.push('Repeat ID front');
  }

  if (match['document-back-photo-url']) {
    rightImageUrls.push(match['document-back-photo-url']);
    rightImageFooterTexts.push('Repeat ID back');
  }

  return (
    <ImagesComparison
      description='Submitted images on left, matched ID on right'
      leftImagesUrls={leftImageUrls}
      leftImagesFooterTexts={leftImageFooterTexts}
      rightImagesUrls={rightImageUrls}
      rightImagesFooterTexts={rightImageFooterTexts}
    />
  );
}
